// The partner portal's own Neon Auth client (MOTHER-69). It lives under src/partners/,
// not src/auth/, because the partner bundle imports nothing of Mothership's
// (partnerBundle.test.ts). Partners sign in with Google, or with an email and password
// whose address is confirmed by an emailed six-digit code; /api/partner/* accepts only
// a session whose email is verified (apps/server/src/auth.mjs).
import { createAuthClient } from '@neondatabase/neon-js/auth';

/** Injected by the server's static handler (apps/server/src/static.mjs); the two names must match. */
const NEON_AUTH_URL_META_SELECTOR = 'meta[name="mothership-neon-auth-url"]';

/** A Neon Auth (Better Auth) failure: its HTTP status, its message and its error code. */
export class PartnerAuthError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'PartnerAuthError';
  }
}

export interface PartnerAuth {
  /** Sends the browser to Google and back to this page signed in; settles only on failure. */
  signInWithGoogle(): Promise<void>;
  signInWithEmail(email: string, password: string): Promise<void>;
  signUpWithEmail(input: { name: string; email: string; password: string }): Promise<void>;
  /** Confirms the address with the emailed code (and signs in, when Neon Auth does so on verification). */
  verifyEmail(email: string, code: string): Promise<void>;
  /** Emails a new verification code. */
  sendCode(email: string): Promise<void>;
  /** The session token /api/partner/* verifies, or null when nobody is signed in. */
  getToken(): Promise<string | null>;
  signOut(): Promise<void>;
}

/** The Neon Auth client calls PartnerAuth makes; tests pass a stub in its place. */
export interface NeonAuthCalls {
  signIn: {
    social(input: { provider: 'google'; callbackURL: string }): Promise<unknown>;
    email(input: { email: string; password: string }): Promise<unknown>;
  };
  signUp: { email(input: { name: string; email: string; password: string }): Promise<unknown> };
  emailOtp: {
    verifyEmail(input: { email: string; otp: string }): Promise<unknown>;
    sendVerificationOtp(input: { email: string; type: 'email-verification' }): Promise<unknown>;
  };
  getSession(): Promise<unknown>;
  signOut(): Promise<unknown>;
}

/**
 * PartnerAuth over a Neon Auth client. `origin` is this page's own origin: Google sends
 * the person back to it (`/`), never to Mothership's host.
 */
export function createPartnerAuth(client: NeonAuthCalls, origin: string): PartnerAuth {
  return {
    async signInWithGoogle() {
      // Neon Auth appends its session verifier to this URL; getSession() picks it up on return.
      const callbackURL = new URL('/', origin).href;
      throwIfError(await client.signIn.social({ provider: 'google', callbackURL }), 'Unable to start Google sign-in.');
    },
    async signInWithEmail(email, password) {
      throwIfError(await client.signIn.email({ email, password }), 'Unable to sign in.');
    },
    async signUpWithEmail(input) {
      throwIfError(await client.signUp.email(input), 'Unable to create your account.');
    },
    async verifyEmail(email, code) {
      throwIfError(await client.emailOtp.verifyEmail({ email, otp: code }), 'That code did not work.');
    },
    async sendCode(email) {
      throwIfError(
        await client.emailOtp.sendVerificationOtp({ email, type: 'email-verification' }),
        'Unable to send a new code.',
      );
    },
    async getToken() {
      const response = await client.getSession();
      throwIfError(response, 'Unable to read your session.');
      const data = isRecord(response) ? response.data : undefined;
      const session = isRecord(data) ? data.session : undefined;
      const token = isRecord(session) ? session.token : undefined;
      return typeof token === 'string' && token.length > 0 ? token : null;
    },
    async signOut() {
      throwIfError(await client.signOut(), 'Unable to sign out.');
    },
  };
}

/** Signing in with an address whose code was never entered. */
export function isEmailNotVerified(error: unknown): boolean {
  return error instanceof PartnerAuthError && error.code === 'EMAIL_NOT_VERIFIED';
}

/** The error in a Better Auth `{ data, error }` response, or null when the call succeeded. */
export function authErrorOf(response: unknown, fallback: string): PartnerAuthError | null {
  if (!isRecord(response) || response.error === null || response.error === undefined) return null;
  const error = response.error;
  if (!isRecord(error)) return new PartnerAuthError(503, fallback);
  const status = typeof error.status === 'number' && error.status > 0 ? error.status : 503;
  const message = typeof error.message === 'string' && error.message.length > 0 ? error.message : fallback;
  const code = typeof error.code === 'string' && error.code.length > 0 ? error.code : undefined;
  return new PartnerAuthError(status, message, code);
}

function throwIfError(response: unknown, fallback: string): void {
  const error = authErrorOf(response, fallback);
  if (error !== null) throw error;
}

/**
 * This environment's Neon Auth URL: the server's injected meta first (one image runs
 * in every environment), else the build-time VITE_NEON_AUTH_URL, else none.
 */
export function resolveNeonAuthUrl(
  page: Pick<ParentNode, 'querySelector'> | undefined,
  buildTimeUrl: string | undefined,
): string | undefined {
  const injected = page?.querySelector(NEON_AUTH_URL_META_SELECTOR)?.getAttribute('content')?.trim();
  if (injected !== undefined && injected.length > 0) return injected;
  const buildTime = buildTimeUrl?.trim();
  return buildTime === undefined || buildTime.length === 0 ? undefined : buildTime;
}

const notConfigured = (): Promise<never> =>
  Promise.reject(new PartnerAuthError(503, 'Sign-in is not configured for this environment.'));

/** Stands in when no Neon Auth URL reached the page, so each form can say why it cannot work. */
const unconfigured: PartnerAuth = {
  signInWithGoogle: notConfigured,
  signInWithEmail: notConfigured,
  signUpWithEmail: notConfigured,
  verifyEmail: notConfigured,
  sendCode: notConfigured,
  async getToken() {
    return null;
  },
  async signOut() {},
};

/** The page's PartnerAuth: built once, on first use, so importing this file in a test touches no browser. */
let pageAuth: PartnerAuth | undefined;
export function partnerAuth(): PartnerAuth {
  if (pageAuth === undefined) {
    const url = resolveNeonAuthUrl(document, import.meta.env.VITE_NEON_AUTH_URL);
    if (url === undefined) {
      pageAuth = unconfigured;
    } else {
      // Built on its own line: passed straight in, NeonAuthCalls would steer its generic.
      const client = createAuthClient(url);
      pageAuth = createPartnerAuth(client, window.location.origin);
    }
  }
  return pageAuth;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
